import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import "@/App.css";
import { AuthProvider } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { TenantGuard } from "@/components/TenantGuard";
import HostGate from "@/components/HostGate";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import MatchesPage from "@/pages/MatchesPage";
import MatchDetailPage from "@/pages/MatchDetailPage";
import LeaderboardPage from "@/pages/LeaderboardPage";
import AdminPage from "@/pages/AdminPage";
import EditorPage from "@/pages/EditorPage";
import KeyMonitorsPage from "@/pages/KeyMonitorsPage";
import TermsPage from "@/pages/TermsPage";
import PrivacyPage from "@/pages/PrivacyPage";
import CookiesPage from "@/pages/CookiesPage";
import ChampionsPage from "@/pages/ChampionsPage";
import SeasonsArchivePage from "@/pages/SeasonsArchivePage";
import SeasonArchiveDetailPage from "@/pages/SeasonArchiveDetailPage";
import LeaguesPage from "@/pages/LeaguesPage";
import LeagueDetailPage from "@/pages/LeagueDetailPage";
import PaymentsSuccessPage from "@/pages/PaymentsSuccessPage";
import PaymentsCancelPage from "@/pages/PaymentsCancelPage";
import MyEntriesPage from "@/pages/MyEntriesPage";
import SettingsPage from "@/pages/SettingsPage";
import MyVouchersPage from "@/pages/MyVouchersPage";
import UnsubscribePage from "@/pages/UnsubscribePage";
import BusinessPubsPage from "@/pages/BusinessPubsPage";
import BusinessDemoPage from "@/pages/BusinessDemoPage";
import StaffRedemptionPage from "@/pages/StaffRedemptionPage";
import ClientLandingPage from "@/pages/ClientLandingPage";
import ClientManagerPage from "@/pages/ClientManagerPage";
import ClientDashboardPage from "@/pages/ClientDashboardPage";
import ClientHomeRedirect from "@/pages/ClientHomeRedirect";
import ClientSlugHome from "@/pages/ClientSlugHome";
import SluggedLoginPage from "@/pages/SluggedLoginPage";
import AmblemeadLandingPage from "@/pages/AmblemeadLandingPage";
import PromoVideoPage from "@/pages/PromoVideoPage";

function PromoRedirect() {
  window.location.replace(`${process.env.REACT_APP_BACKEND_URL}/api/promo`);
  return null;
}
import DemoLockGuard from "@/components/DemoLockGuard";
import { useAuth } from "@/context/AuthContext";
import { BrandProvider } from "@/context/BrandContext";
import AgeConfirmDialog from "@/components/AgeConfirmDialog";

/**
 * `/c/:slug/editor` dispatcher — the same URL serves two audiences:
 *   - `role="admin"` or `is_editor=true` → full platform Editor surface
 *     (`EditorPage`). Every write is scoped to the slug via the
 *     `?as_pub=<slug>` / `?as_client=<slug>` interceptors.
 *   - `role="client"` (native brewery / pub owner) → their own
 *     `ClientDashboardPage`. Uses only `/clients/me/*` endpoints so
 *     nothing is gated behind admin/editor role checks.
 * Keeps the URL stable across roles while surfacing the right toolset
 * for each — fixes iter88 test agent finding that client-role users
 * saw "Editor or admin access required" toasts on the shared editor.
 */
function SlugEditorRoute() {
  const { user } = useAuth();
  if (user?.role === "client") return <ClientDashboardPage />;
  return <EditorPage />;
}

/**
 * Root route: unauthenticated visitors land on the login/register page
 * directly (URL stays `/`, so shareable links like the plain domain root
 * feel like a proper landing page). Signed-in users keep their existing
 * "upcoming predictions" experience at `/`.
 */
function RootRoute() {
  const { user, bootstrapped } = useAuth();
  // Wait for the initial /auth/me round-trip to resolve before deciding.
  // Prevents a flash of the login page for returning users with a valid token.
  if (!bootstrapped) return null;
  // iter95.5 — Master surface is ALWAYS reachable for the super-admin.
  // Previous behaviour auto-redirected non-admin sessions to their own
  // slug (pub_slug/client_slug/is_editor branches), which trapped the
  // super-admin whenever a stale slug-editor cookie existed in the same
  // browser. Now we render the master login regardless, and let a
  // "session chooser" chip on the LoginPage give non-admins an explicit
  // "continue to your dashboard" OR "log out" choice.
  if (!user) return <LoginPage masterOnly />;
  // Super-Admin (role="admin") always stays on the platform-wide root —
  // even if their legacy account happens to have a `pub_slug` attached.
  if (user.role === "admin") return <MatchesPage />;
  // Every other logged-in user (regular player, client admin, slug
  // editor, platform editor) — render the master login with the session
  // chooser so they can pick their surface consciously.
  return <LoginPage masterOnly />;
}

/**
 * Unknown paths inside a client env (`/c/[slug]/anything-not-listed`) drop
 * the user back onto the client home — NOT the platform-wide `/`. This
 * preserves the master-logic rule that "every link inside `/c/[slug]/*`
 * stays within that path".
 */
function SlugCatchAll() {
  const { slug } = useParams();
  return <Navigate to={`/c/${slug}`} replace />;
}

function App() {
  return (
    <div className="App bg-white min-h-screen">
      <BrowserRouter>
        <HostGate>
          <AuthProvider>
            <BrandProvider>
            <DemoLockGuard />
            <Routes>
            <Route path="/login" element={<LoginPage masterOnly />} />
            <Route path="/reset/:token" element={<LoginPage masterOnly />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/cookies" element={<CookiesPage />} />
            <Route path="/unsubscribe" element={<UnsubscribePage />} />
            <Route path="/business/pubs" element={<BusinessPubsPage />} />
            <Route path="/business/demo" element={<BusinessDemoPage />} />
            <Route path="/football-challenge" element={<AmblemeadLandingPage />} />
            <Route path="/admin/promo-video" element={<PromoVideoPage />} />
            <Route path="/promo" element={<PromoRedirect />} />
            <Route path="/redeem/:code" element={<StaffRedemptionPage />} />
            <Route path="/c/:slug" element={<TenantGuard><ClientSlugHome /></TenantGuard>} />
            <Route
              path="/c/:slug/leaderboard"
              element={
                <ProtectedRoute>
                  <TenantGuard><LeaderboardPage /></TenantGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/c/:slug/leagues"
              element={
                <ProtectedRoute>
                  <TenantGuard><LeaguesPage /></TenantGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/c/:slug/leagues/:id"
              element={
                <ProtectedRoute>
                  <TenantGuard><LeagueDetailPage /></TenantGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/c/:slug/matches/:id"
              element={
                <ProtectedRoute>
                  <TenantGuard><MatchDetailPage /></TenantGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/c/:slug/editor"
              element={
                <ProtectedRoute requireClient>
                  <TenantGuard><SlugEditorRoute /></TenantGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/c/:slug/key-monitors"
              element={
                <ProtectedRoute requireEditor>
                  <TenantGuard><KeyMonitorsPage /></TenantGuard>
                </ProtectedRoute>
              }
            />
            {/* Slug-scoped counterparts of the global user pages. When a
                user is inside `/c/[slug]/*`, every link in Header.jsx routes
                to these paths so navigation stays within the client
                environment. The underlying page components are re-used —
                slug/brand context is read from the URL via useParams(). */}
            <Route
              path="/c/:slug/settings"
              element={
                <ProtectedRoute>
                  <TenantGuard><SettingsPage /></TenantGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/c/:slug/my-vouchers"
              element={
                <ProtectedRoute>
                  <TenantGuard><MyVouchersPage /></TenantGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/c/:slug/champions"
              element={
                <ProtectedRoute>
                  <TenantGuard><ChampionsPage /></TenantGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/c/:slug/seasons-archive"
              element={
                <ProtectedRoute>
                  <TenantGuard><SeasonsArchivePage /></TenantGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/c/:slug/seasons-archive/:seasonId"
              element={
                <ProtectedRoute>
                  <TenantGuard><SeasonArchiveDetailPage /></TenantGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/c/:slug/payments/success"
              element={
                <ProtectedRoute>
                  <TenantGuard><PaymentsSuccessPage /></TenantGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/c/:slug/payments/cancel"
              element={
                <ProtectedRoute>
                  <TenantGuard><PaymentsCancelPage /></TenantGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/c/:slug/entries"
              element={
                <ProtectedRoute>
                  <TenantGuard><MyEntriesPage /></TenantGuard>
                </ProtectedRoute>
              }
            />
            <Route path="/c/:slug/terms" element={<TenantGuard><TermsPage /></TenantGuard>} />
            <Route path="/c/:slug/privacy" element={<TenantGuard><PrivacyPage /></TenantGuard>} />
            <Route path="/c/:slug/cookies" element={<TenantGuard><CookiesPage /></TenantGuard>} />
            <Route path="/c/:slug/unsubscribe" element={<TenantGuard><UnsubscribePage /></TenantGuard>} />
            <Route path="/c/:slug/register" element={<TenantGuard><RegisterPage /></TenantGuard>} />
            {/* Legacy dashboard route — kept as the safe fallback when a client
                has no pubs yet (used as ClientHomeRedirect's fallback target). */}
            <Route
              path="/client"
              element={
                <ProtectedRoute requireClient>
                  <ClientDashboardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/editor/clients"
              element={
                <ProtectedRoute requireAdmin>
                  <ClientManagerPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <SettingsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/my-vouchers"
              element={
                <ProtectedRoute>
                  <MyVouchersPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/champions"
              element={
                <ProtectedRoute>
                  <ChampionsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/seasons-archive"
              element={
                <ProtectedRoute>
                  <SeasonsArchivePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/seasons-archive/:seasonId"
              element={
                <ProtectedRoute>
                  <SeasonArchiveDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/leagues"
              element={
                <ProtectedRoute>
                  <LeaguesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/leagues/:id"
              element={
                <ProtectedRoute>
                  <LeagueDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/payments/success"
              element={
                <ProtectedRoute>
                  <PaymentsSuccessPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/payments/cancel"
              element={
                <ProtectedRoute>
                  <PaymentsCancelPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/entries"
              element={
                <ProtectedRoute>
                  <MyEntriesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/"
              element={<RootRoute />}
            />
            <Route
              path="/matches/:id"
              element={
                <ProtectedRoute>
                  <MatchDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/leaderboard"
              element={
                <ProtectedRoute>
                  <LeaderboardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin"
              element={
                <ProtectedRoute requireAdmin>
                  <AdminPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/editor"
              element={
                <ProtectedRoute requireEditor>
                  <EditorPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/key-monitors"
              element={
                <ProtectedRoute requireEditor>
                  <KeyMonitorsPage />
                </ProtectedRoute>
              }
            />
            <Route path="/:slug" element={<SluggedLoginPage />} />
            <Route path="/c/:slug/*" element={<SlugCatchAll />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <AgeConfirmDialog />
          <Toaster position="top-right" />
          </BrandProvider>
        </AuthProvider>
        </HostGate>
      </BrowserRouter>
    </div>
  );
}

export default App;
